import classNames from 'classnames'
import { Cross, Menu, Search } from 'components/Icons'
import style from 'components/Navigation.module.scss'
import SearchPanel from 'components/SearchPanel'
import { AnimatePresence, motion } from 'framer-motion'
import { Link } from 'gatsby'
import { useStores } from 'hooks/useStores'
import { transitionDefaults } from 'lib/constants'
import { observer } from 'mobx-react'
import React, { useState } from 'react'

const Navigation = ({ navigation, allResources }) => {
  const { uiStore } = useStores()

  const [isActive, setIsActive] = useState(false)
  const [isSearchActive, setIsSearchActive] = useState(false)

  const menuVariants = {
    initial: custom => ({
      opacity: 0,
      transition: transitionDefaults,
    }),
    animate: custom => ({
      opacity: 1,

      transition: transitionDefaults,
    }),
    exit: custom => ({
      opacity: 0,

      transition: transitionDefaults,
    }),
  }

  const menuItemVariants = {
    initial: custom => ({
      opacity: 0,
      y: -20,
      transition: transitionDefaults,
    }),
    animate: custom => ({
      opacity: 1,
      y: 0,
      transition: transitionDefaults,
    }),
    exit: custom => ({
      opacity: 0,
      y: -20,
      transition: transitionDefaults,
    }),
  }

  /*
		Data
	*/

  // Remove duplicates to display unique navigation elements
  const pages = navigation.nodes.filter((nav, index, self) =>
    self.findIndex(t => t.slug === nav.slug) === index
  )

  /*
		Menu
	*/

  const handleMenuToggle = () => {
    setIsSearchActive(false)
    setIsActive(!isActive)
  }

  const handleSearchToggle = () => {
    setIsActive(false)
    setIsSearchActive(old => !old)
  }

  const handleClose = () => {
    setIsActive(false)
  }

  const searchToggleClassName = classNames(style.searchToggleButton, {
    [style.searchToggleButton___isActive]: isSearchActive,
  })

  return (
    <div className={style.root}>
      <nav className={style.nav}>
        <button
          className={style.menuHeader}
          aria-expanded={isActive}
          onClick={handleMenuToggle}
          type="button"
          aria-controls="menu"
        >
          {uiStore.currentPage && (
            <div className={style.currentPage}>
              {uiStore.currentPage?.title}
            </div>
          )}
          <div className={style.menuIcon}>
            {isActive ? <Cross /> : <Menu />}
          </div>
        </button>
        <AnimatePresence initial={false}>
          {(isActive || !uiStore.isSmallerThanTablet) && (
            <motion.ul
              className={style.menu}
              variants={menuVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              role="menu"
              id="menu"
            >
              {pages.map(page => {
                return (
                  <motion.li
                    key={page.id}
                    className={style.menuItem}
                    role="presentation"
                    variants={menuItemVariants}
                  >
                    <Link
                      to={`${
                        page.homepage ? '/' : `/${page.slug}`
                      }`}
                      className={style.menuLink}
                      activeClassName={style.menuLink___isActive}
                      role="menuitem"
                      onClick={handleClose}
                    >
                      <div className={style.menuLinkLabel}>{page.title}</div>
                    </Link>
                  </motion.li>
                )
              })}
              <motion.li
                className={[style.menuItem, style.menuItem___mobileOnly].join(
                  ' '
                )}
                role="presentation"
                variants={menuItemVariants}
              >
                <a
                  href="https://www.coursera.org/campus/"
                  role="menuitem"
                  className={style.menuLink}
                  onClick={handleClose}
                  target="_blank"
                  rel="noreferrer"
                >
                  Coursera for Campus
                </a>
              </motion.li>
            </motion.ul>
          )}
        </AnimatePresence>
      </nav>

      <button
        className={searchToggleClassName}
        type="button"
        onClick={handleSearchToggle}
      >
        <Search />
      </button>

      <AnimatePresence>
        {isSearchActive && (
          <motion.div
            className={style.search}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ ...transitionDefaults, duration: 0.25 }}
          >
            <SearchPanel allResources={allResources} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default observer(Navigation)
